import { TransactionStatus } from '@features/api/response/UserTransactions';
import { Theme } from '@mui/material';
import GlobalIntlSingleton from '@setup/intl/GlobalIntlSingleton';
import { camelCase } from 'lodash';
import {
  AmountRange,
  AmountRangeKeys,
  SortFilterProperties,
} from './Transactions.types';
import { HeadCell } from '@components/Table/EnhancedTableHead';

const translationBase = 'portfolio.transactions';
const translationBaseStatus = `${translationBase}.transactionStatus`;
const translationBaseStatusTooltip = `${translationBase}.transactionStatusTooltips`;
const translationBaseAmountRange = `${translationBase}.amountRanges`;

export const getStatusProps = (status: TransactionStatus, theme: Theme) => {
  const camelCasedStatus = camelCase(status);
  switch (status) {
    case TransactionStatus.Invested:
    case TransactionStatus.AwaitingFirstClose: {
      return {
        color: theme.palette.success.main,
        label: GlobalIntlSingleton.intl.formatMessage({
          id: `${translationBaseStatus}.${camelCasedStatus}`,
        }),
      };
    }
    case TransactionStatus.AwaitingPayment: {
      return {
        color: theme.palette.success.main,
        label: GlobalIntlSingleton.intl.formatMessage({
          id: `${translationBaseStatus}.${camelCasedStatus}`,
        }),
        tooltip: GlobalIntlSingleton.intl.formatMessage({
          id: `${translationBase}.paymentInstructions.clickToSeeInstructions`,
        }),
      };
    }
    case TransactionStatus.Approved: {
      return {
        color: theme.palette.success.main,
        label: GlobalIntlSingleton.intl.formatMessage({
          id: `${translationBaseStatus}.${camelCasedStatus}`,
        }),
        tooltip: GlobalIntlSingleton.intl.formatMessage({
          id: `${translationBase}.approvedDisclaimer`,
        }),
      };
    }
    case TransactionStatus.PendingCustomerSignature: {
      return {
        color: theme.palette.warning.main,
        label: GlobalIntlSingleton.intl.formatMessage({
          id: `${translationBaseStatus}.${camelCasedStatus}`,
        }),
        tooltip: GlobalIntlSingleton.intl.formatMessage({
          id: `${translationBaseStatusTooltip}.${camelCasedStatus}`,
        }),
      };
    }
    case TransactionStatus.InReview: {
      return {
        color: theme.palette.warning.main,
        label: GlobalIntlSingleton.intl.formatMessage({
          id: `${translationBaseStatus}.${camelCasedStatus}`,
        }),
        tooltip: GlobalIntlSingleton.intl.formatMessage({
          id: `${translationBase}.reviewDisclaimer`,
        }),
      };
    }
    case TransactionStatus.Declined: {
      return {
        color: theme.palette.error.main,
        label: GlobalIntlSingleton.intl.formatMessage({
          id: `${translationBaseStatus}.${camelCasedStatus}`,
        }),
      };
    }
    default: {
      return {
        color: theme.palette.info.main,
        label: '',
      };
    }
  }
};

export const headerCells: readonly HeadCell<SortFilterProperties>[] = [
  {
    id: 'productName',
    numeric: false,
    disablePadding: false,
    label: GlobalIntlSingleton.intl.formatMessage({
      id: `${translationBase}.product`,
    }),
  },
  {
    id: 'type',
    numeric: false,
    disablePadding: false,
    label: GlobalIntlSingleton.intl.formatMessage({
      id: `${translationBase}.type`,
    }),
  },
  {
    id: 'subscriptionDate',
    numeric: false,
    disablePadding: false,
    label: GlobalIntlSingleton.intl.formatMessage({
      id: `${translationBase}.subscriptionDate`,
    }),
  },
  {
    id: 'investmentDate',
    numeric: false,
    disablePadding: false,
    label: GlobalIntlSingleton.intl.formatMessage({
      id: `${translationBase}.investmentDate`,
    }),
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: GlobalIntlSingleton.intl.formatMessage({
      id: `${translationBase}.status`,
    }),
  },
  {
    id: 'amount',
    numeric: true,
    disablePadding: false,
    label: GlobalIntlSingleton.intl.formatMessage({
      id: `${translationBase}.amount`,
    }),
  },
];

export const amountFilterRanges: { [key in AmountRangeKeys]: AmountRange } = {
  '10kTo50k': {
    from: 10000,
    to: 50000,
  },
  '50kTo100k': {
    from: 50000,
    to: 100000,
  },
  '100kTo250k': {
    from: 100000,
    to: 250000,
  },
  '250kToInfinity': {
    from: 250000,
  },
};

export const amountRangeMenuItems: { name: string; value: AmountRangeKeys }[] =
  [
    {
      name: GlobalIntlSingleton.intl.formatMessage({
        id: `${translationBaseAmountRange}.10kTo50k`,
      }),
      value: '10kTo50k',
    },
    {
      name: GlobalIntlSingleton.intl.formatMessage({
        id: `${translationBaseAmountRange}.50kTo100k`,
      }),
      value: '50kTo100k',
    },
    {
      name: GlobalIntlSingleton.intl.formatMessage({
        id: `${translationBaseAmountRange}.100kTo250k`,
      }),
      value: '100kTo250k',
    },
    {
      name: GlobalIntlSingleton.intl.formatMessage({
        id: `${translationBaseAmountRange}.250kToInfinity`,
      }),
      value: '250kToInfinity',
    },
  ];
